import { TemplateConfig } from "@/interfaces/editor";
import { Project } from "./Project";
import { UserDevice } from "./UserDevice";
import { CloudResponse, EmptyResponse } from "@/service-worker/sync/CloudResponse";
import { BtDiskFileUtils } from "./BtDiskFileUtils";
import { BtDraftMigrator } from "./BtDraftMigrator";
import { BtDraftsManager } from "./BtDraftsManager";


export class CloudTempProject extends Project {
    type = "CloudTempProject";

    static get baseUrl(): URL {
        // const url = new URL(FileManager.default.temporaryDirectory.toString());
        const url = new URL('https://bazaart.com');
        url.pathname = `${url.pathname}sync/projects`;
        return url;
    }

    static relativeToProjectsPath(path: string, deviceId?: string): string {
        if (deviceId !== undefined && deviceId !== UserDevice.current.id) {
            throw new Error("Can't get local path of other devices");
        }

        const base = CloudTempProject.baseUrl.pathname;
        let relative = path.replace(base, "");
        relative = relative.replace(CloudTempProject.baseUrl.toString(), "");
        let relativeNoSuffixSlash = relative.replace(/^\/+/, '');
        return relativeNoSuffixSlash;
    }

    constructor(projectId: string) {
        super(projectId)
    }

        
    async prepareForUpload() {
        await this.clear();

        if (!this.originalProject) {
            console.error("sync: has no, or couldn't load the local project \(projectId)")
            return
        }

        let currentDraftPath = this.originalProject?.draftPath;
        if (!currentDraftPath){
            return;
        }
        
        await BtDiskFileUtils.duplicate([currentDraftPath], [this.getProjectUrl().pathname]);
        
        await this.cleanDuplicatedFiles(true)
    }
    
    // get projectUrl(): URL {
    //     return new URL(`${CloudTempProject.baseUrl}/${this.projectId}`);
    // }

    getProjectUrl(): URL {
        return new URL(`${CloudTempProject.baseUrl}/${this.projectId}`);
    }

    getDraftPathForProjectId(projectId: string): string | null {
        return `/sync/projects/${projectId}`;
    }

    get originalProject(): Project {
        let p = BtDraftsManager.get(this.projectId)
        return p
    }

    async clear(): Promise<void> {
        try {
            return BtDiskFileUtils.deleteFiles([this.getProjectUrl().pathname]);
        } catch (error) {
            console.warn(`sync: failed to delete temp projects folder: ${error.message}`);
        }
    }

    async saveToProjects(updateVersion: boolean, afterProject: Project | null): Promise<CloudResponse<[Project, any[]]> | EmptyResponse> {
        const currentDraftPath = this.originalProject?.draftPath;


        let newProjectConfig = await this.loadProjectConfig();
        let newProject = BtDraftsManager.createDraftFromTemplateConfig(newProjectConfig);

        if (!newProject) {
            return EmptyResponse.sendError(`sync: failed to load temp downloaded project: ${this.projectId}`);
        }

        this.deleteEmptyDirectories();

        if (currentDraftPath) {
            // Rename current project directory
            const renamedDraftPath = `${currentDraftPath.split('/').slice(0, -1).join('/')}/_${this.projectId}`
    
            try {
                await BtDiskFileUtils.moveFiles([currentDraftPath], [renamedDraftPath]);
            } catch (error) {
                throw new Error(`sync: error - failed to rename existing project folder: ${this.projectId}. error: ${(error as Error).message}`);
            }
    
            // Move new project directory from temp to projects directory
            try {
                await BtDiskFileUtils.moveFiles([this.getProjectUrl().pathname], [currentDraftPath]);
            } catch (error) {
                // Take back the old renamed project directory
                try {
                    await BtDiskFileUtils.moveFiles([renamedDraftPath], [currentDraftPath]);
                } catch {}
                throw new Error(`sync: error - failed to move downloaded project from temp to projects folder. project: ${this.projectId}. error: ${(error as Error).message}`);
            }
    
            // no need to remove previous downloaded files as we only store the previous latest values and this will reset them for now reason.
            // try {
                // Delete old renamed project directory
                // await BtDiskFileUtils.deleteFiles([renamedDraftPath]);
            // } catch (error) {
            //     console.warn(`sync: failed to delete old project directory of: ${renamedDraftPath}. error: ${(error as Error).message}`);
            // }
        } else {
            const destPath = newProject.draftPath;
            if (!destPath) {
                throw new Error("sync: error - failed to get destination path for new fetched project");
            }
    
            try {
                await BtDiskFileUtils.moveFiles([this.getProjectUrl().pathname], [destPath]);
            } catch (error) {
                throw new Error(`sync: error - failed to move downloaded project from temp to projects folder. project: ${this.projectId}. error: ${(error as Error).message}`);
            }
        }

        if (updateVersion) {
            BtDraftsManager.saveDraft(newProject);
        } else if (afterProject) {
            BtDraftsManager.updateDraftsWithDraft(newProject, afterProject);
        } else {
            BtDraftsManager.updateDrafts(newProject);
        }
        // WorkerBtDraftsManager.updateScreenshotCache(newProject);

        let migrator = new BtDraftMigrator();
        const migrationResult = await migrator.asyncMigrate(newProject);
        if (!migrationResult.success) {
            return EmptyResponse.sendError(`sync: failed to migrate downloaded project: ${this.projectId}`);
        }

        if (newProjectConfig.screenshotDate){
            newProjectConfig.screenshotDate = new Date(newProjectConfig.screenshotDate)
        }
        if (newProjectConfig.draftDate){
            newProjectConfig.draftDate = new Date(newProjectConfig.draftDate)
        }
        if (newProjectConfig.draftCreationDate){
            newProjectConfig.draftCreationDate = new Date(newProjectConfig.draftCreationDate)
        }

        const newLayers = await newProject.loadLayersConfig();
        let projectInfo = [newProject, newLayers]
        let response = {
            succeed: true,
            value: projectInfo
        } as CloudResponse<[Project, any[]]>
        return response
    }

    private deleteEmptyDirectories(): void {
        try {
            const emptyDirectories = BtDiskFileUtils.allEmptyDirectories(this.getProjectUrl().pathname);
            if (emptyDirectories.length > 0) {
                console.log(`sync: deleting ${emptyDirectories.length} empty directories`);
                BtDiskFileUtils.deleteFiles(emptyDirectories.map(dir => dir));
            }
        } catch (error) {
            console.warn(`sync: failed to delete empty project directories of: ${this.getProjectUrl()}. error: ${error.message}`);
        }
    }
    
    deconstructor() {
        this.clear();
    }
}
